import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar, Clock, MapPin, User, FileText, Upload, Plus, Download, LogOut, ChevronLeft, ChevronRight, ExternalLink, Navigation, CalendarDays, CalendarRange, Copy, Apple, Smartphone, Palette, Car, Building2, Euro, Edit, X, Trash2, Video, Link2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';

// Hours for week view
const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7h to 18h

// Couleurs disponibles pour les techniciens
const TECHNICIAN_COLORS = [
  { bg: "#3B82F6", text: "#FFFFFF", name: "Bleu" },
  { bg: "#EF4444", text: "#FFFFFF", name: "Rouge" },
  { bg: "#F59E0B", text: "#1A1A1A", name: "Jaune" },
  { bg: "#10B981", text: "#FFFFFF", name: "Vert" },
  { bg: "#8B5CF6", text: "#FFFFFF", name: "Violet" },
  { bg: "#EC4899", text: "#FFFFFF", name: "Rose" },
  { bg: "#06B6D4", text: "#FFFFFF", name: "Cyan" },
  { bg: "#F97316", text: "#FFFFFF", name: "Orange" },
  { bg: "#6366F1", text: "#FFFFFF", name: "Indigo" },
  { bg: "#84CC16", text: "#1A1A1A", name: "Lime" },
];

// Types de jours spéciaux (non comptés comme interventions)
const SPECIAL_TYPES = {
  off: { label: "OFF / Congé", bg: "#6B7280", text: "#FFFFFF" },
  office: { label: "Bureau", bg: "#2563EB", text: "#FFFFFF" },
  exceptional: { label: "Exceptionnel", bg: "#7C3AED", text: "#FFFFFF" },
};

function Planning() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [missions, setMissions] = useState([]);
  const [visioSessions, setVisioSessions] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTechnician, setSelectedTechnician] = useState('all');
  const [showNewMission, setShowNewMission] = useState(false);
  const [showVisioDialog, setShowVisioDialog] = useState(false);
  const [creatingVisio, setCreatingVisio] = useState(false);
  const [visioForm, setVisioForm] = useState({
    client_name: '', client_phone: '',
    scheduled_date: new Date().toISOString().slice(0, 10),
    scheduled_time: '', technician_id: '', notes: '',
    intervention_id: '', intervention_label: '',
    donneur_ordre_nom: '', numero_sinistre: '', nom_assurance: '',
    client_email: '', documents: [],
  });
  const [visioInterventions, setVisioInterventions] = useState([]);
  const [uploadingVisioDoc, setUploadingVisioDoc] = useState(false);
  const [showCalendarHelp, setShowCalendarHelp] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [donneursOrdre, setDonneursOrdre] = useState([]);
  const [calendarLinks, setCalendarLinks] = useState({});
  const [pendingFranchises, setPendingFranchises] = useState([]);
  const [editingMission, setEditingMission] = useState(null);
  const [showEditMission, setShowEditMission] = useState(false);
  const [specialDays, setSpecialDays] = useState([]);
  const [showNewSpecialDay, setShowNewSpecialDay] = useState(false);
  const [newSpecialDay, setNewSpecialDay] = useState({
    type: 'off',
    date: new Date().toISOString().split('T')[0],
    end_date: '',
    technician_id: 'all',
    title: '',
    notes: '',
  });
  const [newMission, setNewMission] = useState({
    title: '',
    technician_id: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_time_start: '09:00',
    scheduled_time_end: '11:00',
    address: '',
    client_name: '',
    client_phone: '',
    client_email: '',
    donneur_ordre_id: '',
    numero_sinistre: '',
    numero_dossier: '',
    nom_assurance: '',
    pieces_jointes: [],
    notes: '',
    documents: [],
    recurrence: 'none',
    recurrence_count: 4,
    recurrence_days: [],
  });
  const [uploadingMissionPJ, setUploadingMissionPJ] = useState(false);

  // Get week dates
  const getWeekDates = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  // Get month dates
  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const dates = [];
    
    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      dates.push({ date: d, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days
    const remaining = 42 - dates.length;
    for (let i = 1; i <= remaining; i++) {
      dates.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return dates;
  };

  const weekDates = getWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate);

  useEffect(() => {
    fetchData();
  }, [currentDate, selectedTechnician, viewMode]);

  const fetchData = async () => {
    try {
      let startDate, endDate;
      if (viewMode === 'week') {
        startDate = weekDates[0].toISOString().split('T')[0];
        endDate = weekDates[6].toISOString().split('T')[0];
      } else {
        startDate = monthDates[0].date.toISOString().split('T')[0];
        endDate = monthDates[monthDates.length - 1].date.toISOString().split('T')[0];
      }

      const [missionsRes, techRes, donneursRes, franchisesRes, specialRes, visioRes, interventionsRes] = await Promise.all([
        axios.get(`${API}/planning`, { 
          params: { 
            start_date: startDate,
            end_date: endDate,
            technician_id: selectedTechnician !== 'all' ? selectedTechnician : undefined 
          } 
        }),
        axios.get(`${API}/users/technicians`),
        axios.get(`${API}/donneurs-ordre`).catch(() => ({ data: [] })),
        axios.get(`${API}/franchises/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/special-days`, { params: { start_date: startDate, end_date: endDate, technician_id: selectedTechnician !== 'all' ? selectedTechnician : undefined } }).catch(() => ({ data: [] })),
        axios.get(`${API}/visio/sessions`).catch(() => ({ data: [] })),
        axios.get(`${API}/interventions`).catch(() => ({ data: [] }))
      ]);
      setMissions(missionsRes.data);
      setVisioSessions(visioRes.data || []);
      setVisioInterventions(interventionsRes.data || []);
      setTechnicians(techRes.data);
      setDonneursOrdre(donneursRes.data);
      setPendingFranchises(franchisesRes.data || []);
      setSpecialDays(specialRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMission = async () => {
    if (!newMission.title || !newMission.technician_id || !newMission.address) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    if (newMission.recurrence === 'weekdays' && (!newMission.recurrence_days || newMission.recurrence_days.length === 0)) {
      toast.error('Sélectionnez au moins un jour de la semaine');
      return;
    }
    try {
      await axios.post(`${API}/missions`, newMission);
      toast.success('Mission créée');
      setShowNewMission(false);
      resetNewMission();
      fetchData();
    } catch (e) {
      toast.error('Erreur création');
    }
  };

  const resetNewMission = () => {
    setNewMission({
      title: '',
      technician_id: '',
      scheduled_date: currentDate.toISOString().split('T')[0],
      scheduled_time_start: '09:00',
      scheduled_time_end: '11:00',
      address: '',
      client_name: '',
      client_phone: '',
      client_email: '',
      donneur_ordre_id: '',
      numero_sinistre: '',
      numero_dossier: '',
      nom_assurance: '',
      pieces_jointes: [],
      notes: '',
      documents: [],
      recurrence: 'none',
      recurrence_count: 4,
      recurrence_days: [],
    });
  };

  // Jours spéciaux
  const getSpecialDaysForDate = (date) => {
    const ds = date.toISOString().split('T')[0];
    return specialDays.filter(sd => (sd.date || '') <= ds && (sd.end_date || sd.date || '') >= ds);
  };

  const handleCreateSpecialDay = async () => {
    if (!newSpecialDay.date) { toast.error('Date requise'); return; }
    try {
      await axios.post(`${API}/special-days`, {
        ...newSpecialDay,
        end_date: newSpecialDay.end_date || newSpecialDay.date,
        technician_id: newSpecialDay.technician_id === 'all' ? null : newSpecialDay.technician_id,
      });
      toast.success('Jour spécial ajouté');
      setShowNewSpecialDay(false);
      setNewSpecialDay({ type: 'off', date: currentDate.toISOString().split('T')[0], end_date: '', technician_id: 'all', title: '', notes: '' });
      fetchData();
    } catch (e) { toast.error('Erreur création'); }
  };

  const deleteSpecialDay = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.delete(`${API}/special-days/${id}`);
      toast.success('Jour spécial supprimé');
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  // Poser un jour OFF/spécial directement depuis une case du planning
  const openSpecialDayForDate = (date, e) => {
    if (e) e.stopPropagation();
    if (!canEdit) return;
    const ds = date.toISOString().split('T')[0];
    setNewSpecialDay({
      type: 'off',
      date: ds,
      end_date: '',
      technician_id: selectedTechnician !== 'all' ? selectedTechnician : 'all',
      title: '',
      notes: '',
    });
    setShowNewSpecialDay(true);
  };

  // Modifier une mission existante
  const handleUpdateMission = async () => {
    if (!editingMission) return;
    try {
      await axios.put(`${API}/missions/${editingMission.id}`, editingMission);
      toast.success('Mission modifiée');
      setShowEditMission(false);
      setEditingMission(null);
      fetchData();
    } catch (e) {
      toast.error('Erreur modification');
    }
  };

  const handleDeleteMission = async () => {
    if (!editingMission) return;
    if (!window.confirm(`Supprimer définitivement l'investigation « ${editingMission.title || 'sans titre'} » du planning ?`)) return;
    try {
      await axios.delete(`${API}/missions/${editingMission.id}`);
      toast.success('Investigation supprimée');
      setShowEditMission(false);
      setEditingMission(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Ouvrir le dialog de modification
  const openEditMission = (mission, e) => {
    e.stopPropagation();
    setEditingMission({...mission});
    setShowEditMission(true);
  };

  // Changer la couleur d'un technicien
  const changeTechnicianColor = async (techId, colorIndex) => {
    try {
      await axios.put(`${API}/users/${techId}/color`, { color_index: colorIndex });
      toast.success('Couleur mise à jour');
      fetchData();
      setShowColorPicker(null);
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Calculer le temps de trajet vers une mission
  const getTravelTime = (mission, index, dayMissions) => {
    if (index === 0) return null;
    const prevMission = dayMissions[index - 1];
    if (!prevMission) return null;
    
    // Simple estimation basée sur si les adresses semblent proches ou non
    const prevCity = prevMission.address?.toLowerCase() || '';
    const currCity = mission.address?.toLowerCase() || '';
    
    // Si même ville mentionnée, temps court
    const sameArea = ['paris', 'lyon', 'marseille', 'toulouse', 'nice'].some(
      city => prevCity.includes(city) && currCity.includes(city)
    );
    
    return sameArea ? '~15-30 min' : '~45+ min';
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API}/upload`, formData);
        setNewMission(prev => ({
          ...prev,
          documents: [...prev.documents, res.data.url]
        }));
        toast.success(`${file.name} ajouté`);
      } catch (e) {
        toast.error(`Erreur upload ${file.name}`);
      }
    }
  };

  const getCalendarLink = async (techId) => {
    try {
      const res = await axios.get(`${API}/planning/ical/${techId}`);
      setCalendarLinks(prev => ({ ...prev, [techId]: res.data }));
      toast.success('Lien calendrier généré');
    } catch (e) {
      toast.error('Erreur génération lien');
    }
  };

  const navigatePeriod = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const formatDateHeader = () => {
    if (viewMode === 'week') {
      const start = weekDates[0];
      const end = weekDates[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.getDate()} - ${end.getDate()} ${start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`;
      }
      return `${start.getDate()} ${start.toLocaleDateString('fr-FR', { month: 'short' })} - ${end.getDate()} ${end.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const getMissionsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return missions.filter(m => m.scheduled_date === dateStr);
  };

  const getMissionsForHour = (date, hour) => {
    const dateStr = date.toISOString().split('T')[0];
    return missions.filter(m => {
      if (m.scheduled_date !== dateStr) return false;
      const startHour = parseInt((m.scheduled_time_start || m.scheduled_time || '09:00').split(':')[0]);
      const endHour = parseInt((m.scheduled_time_end || m.scheduled_time_start || m.scheduled_time || '11:00').split(':')[0]);
      return hour >= startHour && hour < endHour;
    });
  };

  const getVisioForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return visioSessions.filter(v => {
      if (v.scheduled_date !== dateStr) return false;
      if (selectedTechnician !== 'all' && v.technician_id !== selectedTechnician) return false;
      return true;
    });
  };

  const getVisioForHour = (date, hour) => {
    return getVisioForDate(date).filter(v => {
      const h = v.scheduled_time ? parseInt(v.scheduled_time.split(':')[0]) : HOURS[0];
      return h === hour;
    });
  };


  const getWazeLink = (address) => {
    return `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  };

  const getGoogleMapsLink = (address) => {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  // Get technician color or default
  const getTechnicianColor = (mission) => {
    if (mission.technician_color) {
      return mission.technician_color;
    }
    // Find technician in list and get their assigned color
    const tech = technicians.find(t => t.id === mission.technician_id);
    if (tech?.color) {
      return tech.color;
    }
    // Default color
    return { bg: '#8B7355', text: '#FFFFFF' };
  };

  const canEdit = user?.role === 'admin' || user?.role === 'secretary';

  const createVisioSession = async () => {
    if (!visioForm.client_name || !visioForm.client_phone || !visioForm.scheduled_date) {
      toast.error('Nom, téléphone et date du client sont requis');
      return;
    }
    setCreatingVisio(true);
    try {
      const payload = { ...visioForm };
      if (!payload.technician_id) delete payload.technician_id;
      if (!payload.intervention_id) { delete payload.intervention_id; delete payload.intervention_label; }
      const res = await axios.post(`${API}/visio/sessions`, payload);
      const link = `${window.location.origin}/visio/${res.data.token}`;
      navigator.clipboard?.writeText(link).catch(() => {});
      toast.success('Session Visio créée — lien client copié dans le presse-papier');
      setShowVisioDialog(false);
      setVisioForm({
        client_name: '', client_phone: '',
        scheduled_date: new Date().toISOString().slice(0, 10),
        scheduled_time: '', technician_id: '', notes: '',
        intervention_id: '', intervention_label: '',
        donneur_ordre_nom: '', numero_sinistre: '', nom_assurance: '',
        client_email: '', documents: [],
      });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la création de la session');
    } finally {
      setCreatingVisio(false);
    }
  };

  const uploadVisioDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Veuillez choisir un fichier PDF'); return; }
    setUploadingVisioDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/upload`, fd);
      setVisioForm((f) => ({ ...f, documents: [...f.documents, { name: file.name, url: up.data.url }] }));
      toast.success('Document ajouté');
    } catch (err) {
      toast.error("Échec de l'ajout du document");
    } finally {
      setUploadingVisioDoc(false);
      e.target.value = '';
    }
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const statusColors = {
    scheduled: 'bg-amber-100 border-amber-300 text-amber-800',
    in_progress: 'bg-blue-100 border-blue-300 text-blue-800',
    completed: 'bg-green-100 border-green-300 text-green-800'
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="KARDIF" className="h-12" />
            <div>
              <h1 className="text-xl font-bold text-[#1A1A1A]">Planning</h1>
              <p className="text-sm text-[#6B7280]">Gestion des missions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setShowCalendarHelp(true)}>
              <Apple className="h-4 w-4 mr-2" />
              Sync Calendrier
            </Button>
            <Button variant="outline" onClick={() => nav(user?.role === 'admin' ? '/admin' : user?.role === 'secretary' ? '/secretary' : '/dashboard')}>
              Retour
            </Button>
            <Button variant="ghost" size="icon" onClick={() => logout()}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <Button 
                variant={viewMode === 'week' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('week')}
                className={viewMode === 'week' ? 'bg-[#8B7355] text-white' : ''}
              >
                <CalendarRange className="h-4 w-4 mr-1" />
                Semaine
              </Button>
              <Button 
                variant={viewMode === 'month' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('month')}
                className={viewMode === 'month' ? 'bg-[#8B7355] text-white' : ''}
              >
                <CalendarDays className="h-4 w-4 mr-1" />
                Mois
              </Button>
            </div>

            <div className="flex items-center gap-1 ml-4">
              <Button variant="outline" size="icon" onClick={() => navigatePeriod(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={goToToday} className="px-4">
                Aujourd'hui
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigatePeriod(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <h2 className="text-lg font-semibold text-[#1A1A1A] ml-4 capitalize">
              {formatDateHeader()}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {/* Légende des techniciens avec sélecteur de couleur */}
            <div className="hidden md:flex items-center gap-2 mr-4">
              {technicians.slice(0, 5).map((tech, i) => {
                const color = tech.color || { bg: '#8B7355', text: '#FFFFFF' };
                return (
                  <div 
                    key={tech.id} 
                    className="flex items-center gap-1 text-xs relative"
                    title={`${tech.name} - Cliquer pour changer la couleur`}
                  >
                    <button
                      onClick={() => setShowColorPicker(showColorPicker === tech.id ? null : tech.id)}
                      className="w-4 h-4 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform cursor-pointer" 
                      style={{ backgroundColor: color.bg }}
                    />
                    <span className="text-[#6B7280] max-w-[60px] truncate">{tech.name.split(' ')[0]}</span>
                    
                    {/* Color picker popup */}
                    {showColorPicker === tech.id && canEdit && (
                      <div className="absolute top-full left-0 mt-2 p-2 bg-white rounded-lg shadow-lg border z-50">
                        <p className="text-xs text-[#6B7280] mb-2">Choisir couleur:</p>
                        <div className="grid grid-cols-5 gap-1">
                          {TECHNICIAN_COLORS.map((c, idx) => (
                            <button
                              key={idx}
                              onClick={() => changeTechnicianColor(tech.id, idx)}
                              className="w-6 h-6 rounded-full border-2 border-white shadow hover:scale-110 transition-transform"
                              style={{ backgroundColor: c.bg }}
                              title={c.name}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {technicians.length > 5 && (
                <span className="text-xs text-[#6B7280]">+{technicians.length - 5}</span>
              )}
            </div>

            <Select value={selectedTechnician} onValueChange={setSelectedTechnician}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Tous les techniciens" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg">
                <SelectItem value="all">Tous les techniciens</SelectItem>
                {technicians.map(t => {
                  const color = t.color || { bg: '#8B7355' };
                  return (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color.bg }} />
                        {t.name}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {canEdit && (
              <Dialog open={showNewMission} onOpenChange={setShowNewMission}>
                <DialogTrigger asChild>
                  <Button className="bg-[#8B7355] hover:bg-[#7A6548] text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvelle mission
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
                  <DialogHeader>
                    <DialogTitle>Créer une mission</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Titre *</Label>
                      <Input
                        value={newMission.title}
                        onChange={(e) => setNewMission({ ...newMission, title: e.target.value })}
                        placeholder="Ex: Recherche fuite - M. Dupont"
                      />
                    </div>
                    <div>
                      <Label>Technicien *</Label>
                      <Select value={newMission.technician_id} onValueChange={(v) => setNewMission({ ...newMission, technician_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border shadow-lg">
                          {technicians.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={newMission.scheduled_date}
                        onChange={(e) => setNewMission({ ...newMission, scheduled_date: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Heure début</Label>
                        <Input
                          type="time"
                          value={newMission.scheduled_time_start}
                          onChange={(e) => setNewMission({ ...newMission, scheduled_time_start: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Heure fin</Label>
                        <Input
                          type="time"
                          value={newMission.scheduled_time_end}
                          onChange={(e) => setNewMission({ ...newMission, scheduled_time_end: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Répétition</Label>
                        <Select value={newMission.recurrence} onValueChange={(v) => setNewMission({ ...newMission, recurrence: v })}>
                          <SelectTrigger data-testid="recurrence-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border shadow-lg">
                            <SelectItem value="none">Aucune</SelectItem>
                            <SelectItem value="weekdays">Jours précis (ex. tous les lundis)</SelectItem>
                            <SelectItem value="weekly">Chaque semaine</SelectItem>
                            <SelectItem value="monthly">Chaque mois</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {newMission.recurrence !== 'none' && (
                        <div>
                          <Label>{newMission.recurrence === 'weekdays' || newMission.recurrence === 'weekly' ? 'Nombre de semaines' : "Nombre d'occurrences"}</Label>
                          <Input type="number" min="1" max="52" value={newMission.recurrence_count}
                            onChange={(e) => setNewMission({ ...newMission, recurrence_count: parseInt(e.target.value || '1', 10) })} data-testid="recurrence-count-input" />
                        </div>
                      )}
                    </div>
                    {newMission.recurrence === 'weekdays' && (
                      <div>
                        <Label>Jours de la semaine</Label>
                        <div className="flex flex-wrap gap-2 mt-1" data-testid="recurrence-days">
                          {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((d, idx) => {
                            const active = (newMission.recurrence_days || []).includes(idx);
                            return (
                              <button
                                key={idx}
                                type="button"
                                data-testid={`recurrence-day-${idx}`}
                                onClick={() => setNewMission({
                                  ...newMission,
                                  recurrence_days: active
                                    ? newMission.recurrence_days.filter((x) => x !== idx)
                                    : [...(newMission.recurrence_days || []), idx],
                                })}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${active ? 'bg-[#8B7355] text-white border-[#8B7355]' : 'bg-white border-[#8B7355]/50 text-[#1A1A1A] hover:border-[#8B7355]'}`}
                              >
                                {d}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-xs text-[#6B7280] mt-1">Ex. cochez Lundi + Mardi et « 8 semaines » pour créer chaque lundi et mardi pendant 8 semaines.</p>
                      </div>
                    )}
                    <div>
                      <Label>Adresse *</Label>
                      <Textarea
                        value={newMission.address}
                        onChange={(e) => setNewMission({ ...newMission, address: e.target.value })}
                        placeholder="Adresse complète"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom client</Label>
                        <Input
                          value={newMission.client_name}
                          onChange={(e) => setNewMission({ ...newMission, client_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input
                          value={newMission.client_phone}
                          onChange={(e) => setNewMission({ ...newMission, client_phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Email client</Label>
                      <Input
                        type="email"
                        value={newMission.client_email}
                        onChange={(e) => setNewMission({ ...newMission, client_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Donneur d'ordre
                      </Label>
                      <Select 
                        value={newMission.donneur_ordre_id || "none"} 
                        onValueChange={(v) => setNewMission({ ...newMission, donneur_ordre_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner (optionnel)" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border shadow-lg">
                          <SelectItem value="none">Aucun</SelectItem>
                          {donneursOrdre.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {donneursOrdre.length === 0 && (
                        <p className="text-xs text-[#6B7280] mt-1">
                          <a href="/donneurs-ordre" className="text-[#8B7355] hover:underline">
                            + Créer un donneur d'ordre
                          </a>
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>N° de sinistre</Label>
                        <Input value={newMission.numero_sinistre} onChange={(e) => setNewMission({ ...newMission, numero_sinistre: e.target.value })} placeholder="SIN-2026-778" data-testid="mission-numero-sinistre-input" />
                      </div>
                      <div>
                        <Label>N° de dossier</Label>
                        <Input value={newMission.numero_dossier} onChange={(e) => setNewMission({ ...newMission, numero_dossier: e.target.value })} placeholder="DOS-99812" data-testid="mission-numero-dossier-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Assurance</Label>
                      <Input value={newMission.nom_assurance} onChange={(e) => setNewMission({ ...newMission, nom_assurance: e.target.value })} placeholder="Ex: AXA Assurances" data-testid="mission-assurance-input" />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2"><FileText className="h-4 w-4" />Documents PDF à faire signer</Label>
                      <p className="text-xs text-[#6B7280] mb-2">Ces PDF seront accessibles au technicien pour les faire signer au client pendant l'intervention.</p>
                      {(newMission.pieces_jointes || []).length > 0 && (
                        <ul className="space-y-1 mb-2" data-testid="mission-attachments-list">
                          {newMission.pieces_jointes.map((a) => (
                            <li key={a.id} className="flex items-center justify-between gap-2 p-2 rounded border bg-gray-50 text-sm">
                              <span className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-red-600" /><span className="truncate">{a.name}</span></span>
                              <button type="button" onClick={() => setNewMission(m => ({ ...m, pieces_jointes: m.pieces_jointes.filter(p => p.id !== a.id) }))} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-[#8B7355]/30 rounded-lg cursor-pointer hover:bg-[#8B7355]/5">
                        <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="mission-attachment-input" disabled={uploadingMissionPJ}
                          onChange={async (e) => {
                            const file = e.target.files?.[0]; e.target.value = '';
                            if (!file) return;
                            setUploadingMissionPJ(true);
                            try {
                              const fd = new FormData(); fd.append('file', file);
                              const res = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
                              const att = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: file.name, url: res.data.url, size: file.size, uploaded_by_name: 'Secrétariat', uploaded_at: new Date().toISOString() };
                              setNewMission(m => ({ ...m, pieces_jointes: [...(m.pieces_jointes || []), att] }));
                            } catch (err) { toast.error('Échec du téléversement'); }
                            setUploadingMissionPJ(false);
                          }} />
                        <Upload className="h-5 w-5 text-[#8B7355]" />
                        <span className="text-sm text-[#6B7280]">{uploadingMissionPJ ? 'Envoi...' : 'Ajouter un PDF'}</span>
                      </label>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={newMission.notes}
                        onChange={(e) => setNewMission({ ...newMission, notes: e.target.value })}
                        placeholder="Instructions particulières..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Documents (ordre de mission, etc.)</Label>
                      <div className="mt-2 space-y-2">
                        {newMission.documents.map((doc, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                            <FileText className="h-4 w-4 text-[#8B7355]" />
                            <span className="text-sm flex-1">Document {i + 1}</span>
                            <Button variant="ghost" size="sm" onClick={() => {
                              setNewMission(prev => ({
                                ...prev,
                                documents: prev.documents.filter((_, idx) => idx !== i)
                              }));
                            }}>×</Button>
                          </div>
                        ))}
                        <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <Upload className="h-5 w-5 text-[#8B7355]" />
                          <span className="text-sm text-[#6B7280]">Ajouter des fichiers</span>
                        </label>
                      </div>
                    </div>
                    <Button onClick={handleCreateMission} className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white">
                      Créer la mission
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {canEdit && (
              <Dialog open={showVisioDialog} onOpenChange={setShowVisioDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355]/10" data-testid="planning-new-visio-btn">
                    <Video className="h-4 w-4 mr-2" />
                    RDV Visio
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-white" data-testid="planning-visio-dialog">
                  <DialogHeader>
                    <DialogTitle>Nouveau RDV Visio-Assistance</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label>Nom du client *</Label>
                      <Input value={visioForm.client_name} onChange={(e) => setVisioForm({ ...visioForm, client_name: e.target.value })} data-testid="planning-visio-client-name" />
                    </div>
                    <div>
                      <Label>Téléphone du client *</Label>
                      <Input value={visioForm.client_phone} onChange={(e) => setVisioForm({ ...visioForm, client_phone: e.target.value })} data-testid="planning-visio-client-phone" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Date *</Label>
                        <Input type="date" value={visioForm.scheduled_date} onChange={(e) => setVisioForm({ ...visioForm, scheduled_date: e.target.value })} data-testid="planning-visio-date" />
                      </div>
                      <div>
                        <Label>Heure</Label>
                        <Input type="time" value={visioForm.scheduled_time} onChange={(e) => setVisioForm({ ...visioForm, scheduled_time: e.target.value })} data-testid="planning-visio-time" />
                      </div>
                    </div>
                    <div>
                      <Label>Technicien</Label>
                      <Select value={visioForm.technician_id} onValueChange={(v) => setVisioForm({ ...visioForm, technician_id: v })}>
                        <SelectTrigger data-testid="planning-visio-tech"><SelectValue placeholder="Moi-même par défaut" /></SelectTrigger>
                        <SelectContent className="bg-white border shadow-lg">
                          {technicians.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Dossier d'intervention (lier au dossier)</Label>
                      <Select value={visioForm.intervention_id} onValueChange={(v) => {
                        const it = visioInterventions.find((x) => x.id === v);
                        setVisioForm({ ...visioForm, intervention_id: v, intervention_label: it ? `${it.numero_dossier || 'Sans n°'} · ${it.nom_sinistre || it.client_nom || ''}`.trim() : '' });
                      }}>
                        <SelectTrigger data-testid="planning-visio-intervention"><SelectValue placeholder="Aucun (optionnel)" /></SelectTrigger>
                        <SelectContent className="bg-white border shadow-lg max-h-64">
                          {visioInterventions.map((it) => (
                            <SelectItem key={it.id} value={it.id}>
                              {(it.numero_dossier || 'Sans n°')} · {(it.nom_sinistre || it.client_nom || '').trim()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Donneur d'ordre</Label>
                        <Input value={visioForm.donneur_ordre_nom} onChange={(e) => setVisioForm({ ...visioForm, donneur_ordre_nom: e.target.value })} placeholder="Ex : DOMUS" data-testid="planning-visio-donneur" />
                      </div>
                      <div>
                        <Label>N° de sinistre</Label>
                        <Input value={visioForm.numero_sinistre} onChange={(e) => setVisioForm({ ...visioForm, numero_sinistre: e.target.value })} data-testid="planning-visio-sinistre" />
                      </div>
                    </div>
                    <div>
                      <Label>Assurance</Label>
                      <Input value={visioForm.nom_assurance} onChange={(e) => setVisioForm({ ...visioForm, nom_assurance: e.target.value })} data-testid="planning-visio-assurance" />
                    </div>
                    <div>
                      <Label>Email du client (envoi du document signé)</Label>
                      <Input type="email" value={visioForm.client_email} onChange={(e) => setVisioForm({ ...visioForm, client_email: e.target.value })} placeholder="client@email.com" data-testid="planning-visio-client-email" />
                    </div>
                    <div>
                      <Label>Documents à faire signer (PDF)</Label>
                      <div className="space-y-1 mt-1">
                        {visioForm.documents.map((d, i) => (
                          <div key={i} className="flex items-center justify-between bg-gray-100 rounded px-2 py-1 text-xs" data-testid={`planning-visio-doc-${i}`}>
                            <span className="truncate">{d.name}</span>
                            <button type="button" onClick={() => setVisioForm({ ...visioForm, documents: visioForm.documents.filter((_, j) => j !== i) })} className="text-red-500 ml-2">✕</button>
                          </div>
                        ))}
                        <label className="flex items-center gap-2 text-sm text-[#8B7355] cursor-pointer">
                          <input type="file" accept="application/pdf" className="hidden" onChange={uploadVisioDoc} data-testid="planning-visio-doc-input" />
                          <span className="inline-flex items-center gap-1 border border-dashed border-[#8B7355] rounded px-3 py-1.5">
                            {uploadingVisioDoc ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ajouter un PDF
                          </span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea value={visioForm.notes} onChange={(e) => setVisioForm({ ...visioForm, notes: e.target.value })} rows={2} data-testid="planning-visio-notes" />
                    </div>
                    <Button onClick={createVisioSession} disabled={creatingVisio} className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white" data-testid="planning-visio-submit">
                      {creatingVisio ? 'Création...' : 'Créer le RDV Visio'}
                    </Button>
                    <p className="text-xs text-[#6B7280] text-center">Le lien client sera copié automatiquement. Retrouvez vos sessions dans le menu « Visio-Assistance ».</p>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {canEdit && (
              <Dialog open={showNewSpecialDay} onOpenChange={setShowNewSpecialDay}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-[#8B7355] text-[#8B7355]" data-testid="new-special-day-btn">
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Jour spécial
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-white">
                  <DialogHeader>
                    <DialogTitle>Ajouter un jour spécial</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div>
                      <Label>Type</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {Object.entries(SPECIAL_TYPES).map(([key, t]) => (
                          <button key={key} type="button" onClick={() => setNewSpecialDay({ ...newSpecialDay, type: key })}
                            data-testid={`special-type-${key}`}
                            className={`p-2 rounded-lg text-xs font-semibold border-2 transition-all ${newSpecialDay.type === key ? 'ring-2 ring-offset-1' : 'opacity-70'}`}
                            style={{ backgroundColor: t.bg, color: t.text, borderColor: t.bg }}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Du</Label>
                        <Input type="date" value={newSpecialDay.date} onChange={(e) => setNewSpecialDay({ ...newSpecialDay, date: e.target.value })} data-testid="special-date-input" />
                      </div>
                      <div>
                        <Label>Au (optionnel)</Label>
                        <Input type="date" value={newSpecialDay.end_date} onChange={(e) => setNewSpecialDay({ ...newSpecialDay, end_date: e.target.value })} data-testid="special-end-date-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Concerne</Label>
                      <Select value={newSpecialDay.technician_id} onValueChange={(v) => setNewSpecialDay({ ...newSpecialDay, technician_id: v })}>
                        <SelectTrigger data-testid="special-tech-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border shadow-lg">
                          <SelectItem value="all">Toute l'équipe</SelectItem>
                          {technicians.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Libellé (optionnel)</Label>
                      <Input value={newSpecialDay.title} onChange={(e) => setNewSpecialDay({ ...newSpecialDay, title: e.target.value })} placeholder="Ex: Congés, Formation, Inventaire..." data-testid="special-title-input" />
                    </div>
                    <Button onClick={handleCreateSpecialDay} className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white" data-testid="save-special-day-btn">
                      Ajouter au planning
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Calendar Views */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : viewMode === 'week' ? (
          /* Week View */
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[800px]">
                <thead>
                  <tr className="border-b">
                    <th className="w-16 p-2 text-xs text-[#6B7280] font-medium"></th>
                    {weekDates.map((date, i) => {
                      const sds = getSpecialDaysForDate(date);
                      return (
                      <th key={i} className={`p-2 text-center border-l ${isToday(date) ? 'bg-[#8B7355]/10' : ''}`}>
                        <button type="button" onClick={() => openSpecialDayForDate(date)} disabled={!canEdit}
                          className={`w-full rounded ${canEdit ? 'hover:bg-[#8B7355]/10 cursor-pointer' : 'cursor-default'}`}
                          title={canEdit ? "Cliquer pour poser un jour OFF / spécial" : undefined} data-testid="week-day-header">
                          <div className="text-xs text-[#6B7280] uppercase">
                            {date.toLocaleDateString('fr-FR', { weekday: 'short' })}
                          </div>
                          <div className={`text-lg font-semibold ${isToday(date) ? 'text-[#8B7355]' : 'text-[#1A1A1A]'}`}>
                            {date.getDate()}
                          </div>
                        </button>
                        {sds.map(sd => {
                          const t = SPECIAL_TYPES[sd.type] || SPECIAL_TYPES.off;
                          return (
                            <div key={sd.id} data-testid="special-day-chip" className="mt-1 rounded px-1 py-0.5 text-[9px] font-semibold flex items-center justify-between gap-1 group"
                              style={{ backgroundColor: t.bg, color: t.text }} title={sd.title || t.label}>
                              <span className="truncate">{sd.title || t.label}{sd.technician_name ? ` · ${sd.technician_name.split(' ')[0]}` : ''}</span>
                              {canEdit && (
                                <button onClick={(e) => deleteSpecialDay(sd.id, e)} className="opacity-0 group-hover:opacity-100 hover:scale-110" title="Supprimer"><X className="h-2.5 w-2.5" /></button>
                              )}
                            </div>
                          );
                        })}
                      </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {HOURS.map(hour => (
                    <tr key={hour} className="border-b hover:bg-gray-50/50">
                      <td className="p-2 text-xs text-[#6B7280] text-right pr-3 align-top">
                        {hour}:00
                      </td>
                      {weekDates.map((date, i) => {
                        const hourMissions = getMissionsForHour(date, hour);
                        const dayMissions = getMissionsForDate(date).sort((a, b) => 
                          (a.scheduled_time_start || '00:00').localeCompare(b.scheduled_time_start || '00:00')
                        );
                        return (
                          <td key={i} className={`border-l p-1 align-top h-16 relative ${isToday(date) ? 'bg-[#8B7355]/5' : ''}`}>
                            {hourMissions.map((mission, j) => {
                              const parseHM = (s) => { const [h, m] = (s || '9:0').split(':').map(Number); return (h || 0) + (m || 0) / 60; };
                              const startHour = parseInt((mission.scheduled_time_start || mission.scheduled_time || '09:00').split(':')[0]);
                              if (startHour !== hour) return null;
                              const techColor = getTechnicianColor(mission);
                              const startDec = parseHM(mission.scheduled_time_start || mission.scheduled_time || '09:00');
                              const endDec = Math.max(startDec + 0.5, parseHM(mission.scheduled_time_end || '11:00'));
                              const ROW = 64;
                              const top = (startDec - hour) * ROW;
                              const height = (endDec - startDec) * ROW - 4;
                              // Gestion des chevauchements: missions démarrant à la même heure côte à côte
                              const sameHour = dayMissions.filter(m => parseInt((m.scheduled_time_start || m.scheduled_time || '09:00').split(':')[0]) === hour);
                              const idx = Math.max(0, sameHour.findIndex(m => m.id === mission.id));
                              const n = Math.max(1, sameHour.length);
                              const widthPct = 100 / n;
                              return (
                                <div
                                  key={j}
                                  className="p-1.5 rounded text-xs border cursor-pointer hover:shadow-lg transition-shadow group absolute overflow-hidden"
                                  style={{
                                    backgroundColor: techColor.bg,
                                    color: techColor.text,
                                    borderColor: techColor.bg,
                                    top: `${top}px`,
                                    height: `${height}px`,
                                    left: `calc(${idx * widthPct}% + 2px)`,
                                    width: `calc(${widthPct}% - 4px)`,
                                    zIndex: 10,
                                  }}
                                  onClick={() => nav(`/intervention/new/${mission.id}`)}
                                  data-testid="planning-mission-block"
                                >
                                    {canEdit && (
                                      <button
                                        onClick={(e) => openEditMission(mission, e)}
                                        className="absolute top-1 right-1 p-1 bg-white/90 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Modifier"
                                      >
                                        <Edit className="h-3 w-3 text-gray-700" />
                                      </button>
                                    )}
                                    <div className="font-medium truncate pr-5">{mission.title}</div>
                                    <div className="flex items-center gap-1 text-[10px] opacity-90">
                                      <Clock className="h-3 w-3" />
                                      {mission.scheduled_time_start || mission.scheduled_time || '09:00'} - {mission.scheduled_time_end || '11:00'}
                                    </div>
                                    {mission.donneur_ordre_nom && (
                                      <div className="text-[9px] opacity-75 truncate flex items-center gap-0.5">
                                        <Building2 className="h-2.5 w-2.5" />
                                        {mission.donneur_ordre_nom}
                                      </div>
                                    )}
                                    <div className="flex items-center justify-between mt-1">
                                      <span className="text-[9px] opacity-75 truncate max-w-[60px]">{mission.technician_name?.split(' ')[0]}</span>
                                      <a
                                        href={getWazeLink(mission.address)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="p-1 bg-white/90 rounded hover:bg-white"
                                        title="Ouvrir dans Waze"
                                      >
                                        <Navigation className="h-3 w-3 text-blue-600" />
                                      </a>
                                    </div>
                                </div>
                              );
                            })}
                            {getVisioForHour(date, hour).map((v, k) => {
                              const parseHM2 = (s) => { const [h, m] = (s || '7:0').split(':').map(Number); return (h || 0) + (m || 0) / 60; };
                              const ROW = 64;
                              const startDec = v.scheduled_time ? parseHM2(v.scheduled_time) : hour;
                              const top = Math.max(0, (startDec - hour) * ROW);
                              return (
                                <div
                                  key={`visio-${v.id}`}
                                  className="p-1 rounded text-[10px] border cursor-pointer hover:shadow-lg transition-shadow absolute overflow-hidden bg-indigo-600 text-white border-indigo-700"
                                  style={{ top: `${top}px`, height: '30px', left: 'calc(58% + 2px)', width: 'calc(42% - 4px)', zIndex: 12 }}
                                  onClick={() => nav(`/visio/session/${v.id}`)}
                                  data-testid="planning-visio-block"
                                  title={`Visio · ${v.client_name}${v.scheduled_time ? ' · ' + v.scheduled_time : ''}`}
                                >
                                  <div className="font-medium truncate flex items-center gap-0.5">
                                    <Video className="h-2.5 w-2.5 shrink-0" />{v.client_name}
                                  </div>
                                </div>
                              );
                            })}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          /* Month View */
          <Card className="overflow-hidden">
            <div className="grid grid-cols-7 border-b">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                <div key={day} className="p-2 text-center text-xs font-medium text-[#6B7280] uppercase">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {monthDates.map(({ date, isCurrentMonth }, i) => {
                const dayMissions = getMissionsForDate(date);
                const daySpecials = getSpecialDaysForDate(date);
                return (
                  <div
                    key={i}
                    onClick={() => openSpecialDayForDate(date)}
                    title={canEdit ? "Cliquer pour poser un jour OFF / spécial" : undefined}
                    className={`min-h-24 p-1 border-b border-r group relative ${canEdit ? 'cursor-pointer hover:bg-[#8B7355]/5' : ''} ${!isCurrentMonth ? 'bg-gray-50' : ''} ${isToday(date) ? 'bg-[#8B7355]/10' : ''}`}
                    data-testid="planning-day-cell"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${!isCurrentMonth ? 'text-[#9CA3AF]' : isToday(date) ? 'text-[#8B7355]' : 'text-[#1A1A1A]'}`}>
                        {date.getDate()}
                      </span>
                      {canEdit && (
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[8px] font-semibold text-[#6B7280] bg-white/80 rounded px-1 flex items-center gap-0.5">
                          <Plus className="h-2.5 w-2.5" />OFF
                        </span>
                      )}
                    </div>
                    {daySpecials.map(sd => {
                      const t = SPECIAL_TYPES[sd.type] || SPECIAL_TYPES.off;
                      return (
                        <div key={sd.id} data-testid="special-day-chip" className="mb-1 rounded px-1 py-0.5 text-[9px] font-semibold flex items-center justify-between gap-1 group"
                          style={{ backgroundColor: t.bg, color: t.text }} title={sd.title || t.label}>
                          <span className="truncate">{sd.title || t.label}{sd.technician_name ? ` · ${sd.technician_name.split(' ')[0]}` : ''}</span>
                          {canEdit && (<button onClick={(e) => deleteSpecialDay(sd.id, e)} className="opacity-0 group-hover:opacity-100"><X className="h-2.5 w-2.5" /></button>)}
                        </div>
                      );
                    })}
                    <div className="space-y-1">
                      {dayMissions.slice(0, 3).map((mission, j) => {
                        const techColor = getTechnicianColor(mission);
                        return (
                          <div
                            key={j}
                            className="p-1 rounded text-xs cursor-pointer hover:shadow-sm transition-shadow"
                            style={{ 
                              backgroundColor: techColor.bg, 
                              color: techColor.text 
                            }}
                            onClick={(e) => { e.stopPropagation(); nav(`/intervention/new/${mission.id}`); }}
                          >
                            <div className="font-medium truncate">{mission.title}</div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] opacity-90">
                                {mission.scheduled_time_start || mission.scheduled_time || '09:00'}
                              </span>
                              <a
                                href={getWazeLink(mission.address)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-0.5 bg-white/80 rounded hover:bg-white"
                                title="Waze"
                              >
                                <Navigation className="h-3 w-3 text-blue-600" />
                              </a>
                            </div>
                          </div>
                        );
                      })}
                      {dayMissions.length > 3 && (
                        <div className="text-[10px] text-[#6B7280] pl-1">
                          +{dayMissions.length - 3} autres
                        </div>
                      )}
                      {getVisioForDate(date).map((v) => (
                        <div
                          key={v.id}
                          className="p-1 rounded text-xs cursor-pointer hover:shadow-sm transition-shadow bg-indigo-600 text-white"
                          onClick={(e) => { e.stopPropagation(); nav(`/visio/session/${v.id}`); }}
                          data-testid="planning-visio-chip"
                          title={`Visio · ${v.client_name}`}
                        >
                          <div className="font-medium truncate flex items-center gap-1">
                            <Video className="h-3 w-3 shrink-0" /> {v.client_name}
                          </div>
                          <span className="text-[10px] opacity-90">{v.scheduled_time || 'Visio'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Mission details on click - could add a side panel here */}
      </main>

      {/* Calendar Sync Help Dialog */}
      <Dialog open={showCalendarHelp} onOpenChange={setShowCalendarHelp}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Apple className="h-5 w-5" />
              Synchroniser avec votre calendrier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#6B7280]">
              Synchronisez le planning KARDIF avec Apple Calendar, Google Calendar ou tout autre calendrier supportant iCal.
            </p>

            <div className="space-y-3">
              <Label>Sélectionnez un technicien pour générer son lien calendrier :</Label>
              {technicians.map(tech => (
                <div key={tech.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[#8B7355]" />
                    <span className="font-medium">{tech.name}</span>
                  </div>
                  {calendarLinks[tech.id] ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(calendarLinks[tech.id].ical_url);
                          toast.success('Lien iCal copié !');
                        }}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copier
                      </Button>
                      <a
                        href={calendarLinks[tech.id].google_calendar_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#8B7355] hover:underline text-sm"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => getCalendarLink(tech.id)}>
                      Générer lien
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <Apple className="h-4 w-4" />
                  Pour Apple Calendar (iPhone/Mac)
                </h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Générez le lien ci-dessus</li>
                  <li>Copiez le lien iCal</li>
                  <li>Sur iPhone : Réglages → Calendrier → Comptes → Ajouter un compte → Autre → Ajouter un calendrier avec abonnement</li>
                  <li>Collez le lien et validez</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Pour Google Calendar
                </h4>
                <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                  <li>Cliquez sur le bouton lien externe après avoir généré</li>
                  <li>Ou collez le lien iCal dans Google Calendar → Autres agendas → À partir de l'URL</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Mission Dialog */}
      <Dialog open={showEditMission} onOpenChange={setShowEditMission}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Modifier la mission</DialogTitle>
          </DialogHeader>
          {editingMission && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={editingMission.title || ''}
                  onChange={(e) => setEditingMission({ ...editingMission, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Technicien *</Label>
                <Select 
                  value={editingMission.technician_id} 
                  onValueChange={(v) => setEditingMission({ ...editingMission, technician_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border shadow-lg">
                    {technicians.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editingMission.scheduled_date || ''}
                  onChange={(e) => setEditingMission({ ...editingMission, scheduled_date: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Heure début</Label>
                  <Input
                    type="time"
                    value={editingMission.scheduled_time_start || editingMission.scheduled_time || '09:00'}
                    onChange={(e) => setEditingMission({ ...editingMission, scheduled_time_start: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Heure fin</Label>
                  <Input
                    type="time"
                    value={editingMission.scheduled_time_end || '11:00'}
                    onChange={(e) => setEditingMission({ ...editingMission, scheduled_time_end: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Adresse</Label>
                <Textarea
                  value={editingMission.address || ''}
                  onChange={(e) => setEditingMission({ ...editingMission, address: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom client</Label>
                  <Input
                    value={editingMission.client_name || ''}
                    onChange={(e) => setEditingMission({ ...editingMission, client_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={editingMission.client_phone || ''}
                    onChange={(e) => setEditingMission({ ...editingMission, client_phone: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editingMission.notes || ''}
                  onChange={(e) => setEditingMission({ ...editingMission, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowEditMission(false); setEditingMission(null); }}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-[#8B7355] hover:bg-[#7A6548] text-white"
                  onClick={handleUpdateMission}
                >
                  Enregistrer
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleDeleteMission}
                data-testid="planning-delete-mission-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer l'investigation
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Planning;
